import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setupManifestAvatarBridge } from '../lib/manifestAvatarBridge';
import { installBuildIntakeBridge } from '../lib/buildIntakeBridge';
import {
  isAvatarFullNavigationState,
  openSplitScreenForSection,
  recoverSplitSectionFromPathname,
  registerAvatarModalHandler,
  setupAvatarEventBridge,
} from '../lib/avatarActions';
import { isEmbeddedSiteView, isStandaloneShareableRoute } from '../lib/avatarEmbed';

interface ManifestAvatarContentBridgeProps {
  onContactClick?: () => void;
  onPartnerClick?: () => void;
}

/** Wires Manifest embed page-action events and selflabs bridge on all public routes */
export default function ManifestAvatarContentBridge({
  onContactClick,
  onPartnerClick,
}: ManifestAvatarContentBridgeProps) {
  const navigate = useNavigate();
  const location = useLocation();

  /** If Milan navigates away from `/`, bounce back and load content in the split panel. */
  useEffect(() => {
    if (isEmbeddedSiteView()) return;
    if (isStandaloneShareableRoute(location.pathname)) return;
    if (location.pathname === '/') return;
    if (isAvatarFullNavigationState(location.state)) return;
    const sectionId = recoverSplitSectionFromPathname(location.pathname);
    if (!sectionId) return;
    navigate('/', { replace: true });
    openSplitScreenForSection(sectionId);
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const teardownManifest = setupManifestAvatarBridge(navigate);
    const teardownBuildIntake = installBuildIntakeBridge();
    const teardownAvatar = setupAvatarEventBridge(navigate);
    const unregisterModal = registerAvatarModalHandler((type) => {
      if (type === 'contact') onContactClick?.();
      if (type === 'partner') onPartnerClick?.();
      if (type === 'demo') onContactClick?.();
    });

    return () => {
      teardownManifest();
      teardownBuildIntake();
      teardownAvatar();
      unregisterModal();
    };
  }, [navigate, onContactClick, onPartnerClick]);

  return null;
}
